import React, { useState } from 'react';
import { FileText, Layers, Image as ImageIcon, Palette, BookOpen } from 'lucide-react';
import { PlanList } from './PlanList';
import { SlideList } from './SlideList';
import { CoverImageUploader } from './CoverImageUploader';
import { BackgroundImageUploader } from './BackgroundImageUploader';
import CourseStandards from './CourseStandards';

type Props = {
  lesson: any;
  plans: any[];
  slides: any[];
  onEnter: (mode: 'plan' | 'ware', id: number) => void;
  onDeletePlan: (id: number) => void;
  onDeleteSlideVersion: (slideId: number, versionId: number) => void;
  onPlanVersionChange: (planId: number) => void;
  currentUserId: number;
  currentUserRole: string;
  planId: number;
  onSlideStatusChange: () => void;
  lessonInfo?: {
    id: number;
    title: string;
    sort_order: number;
  };
  stageInfo?: {
    title: string;
  };
  courseId: number;
};

export const ArtifactTabs: React.FC<Props> = ({ 
  lesson, plans, slides, onEnter, onDeletePlan, onDeleteSlideVersion, onPlanVersionChange, currentUserId, currentUserRole, planId, onSlideStatusChange, lessonInfo, stageInfo, courseId 
}) => {
  // 生成唯一的存储key，包含lessonId以区分不同课程
  const storageKey = `artifactTab_${lesson.id}`;
  
  // 初始加载时从 localStorage 恢复状态
  const [activeTab, setActiveTab] = useState<'plans' | 'slides' | 'cover' | 'background' | 'standards'>(() => {
    const savedTab = localStorage.getItem(storageKey);
    return (savedTab as 'plans' | 'slides' | 'cover' | 'background' | 'standards') || 'plans';
  });
  
  const handleTabChange = (tab: 'plans' | 'slides' | 'cover' | 'background' | 'standards') => {
    setActiveTab(tab);
    localStorage.setItem(storageKey, tab);
  };
  
  // console.log('=== ArtifactTabs 组件 ===');
  // console.log('接收的 plans:', plans);
  // console.log('接收的 slides:', slides);
  // console.log('lesson:', lesson);

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="flex border-b">
        <button onClick={() => handleTabChange('plans')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'plans' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-gray-500 hover:bg-gray-50'}`}><FileText size={16}/> Plans</button>
        <button onClick={() => handleTabChange('slides')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'slides' ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50' : 'text-gray-500 hover:bg-gray-50'}`}><Layers size={16}/> Slides</button>
        <button onClick={() => handleTabChange('standards')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'standards' ? 'text-pink-600 border-b-2 border-pink-600 bg-pink-50/50' : 'text-gray-500 hover:bg-gray-50'}`}><BookOpen size={16}/> 研发标准</button>
        <button onClick={() => handleTabChange('cover')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'cover' ? 'text-green-600 border-b-2 border-green-600 bg-green-50/50' : 'text-gray-500 hover:bg-gray-50'}`}><ImageIcon size={16}/> Cover Image</button>
        <button onClick={() => handleTabChange('background')} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'background' ? 'text-orange-600 border-b-2 border-orange-600 bg-orange-50/50' : 'text-gray-500 hover:bg-gray-50'}`}><Palette size={16}/> BG Image</button>
      </div>
      <div className="p-0">
        {activeTab === 'plans' && (
            <PlanList 
                plans={plans} 
                onEnter={onEnter} 
                onDelete={onDeletePlan} 
            />
        )}
        {activeTab === 'slides' && (
          <SlideList 
            slides={slides} 
            plans={plans}
            onEnter={onEnter} 
            onDeleteSlideVersion={onDeleteSlideVersion} 
            onPlanVersionChange={onPlanVersionChange}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            planId={planId}
            onSlideStatusChange={onSlideStatusChange}
            lessonInfo={lessonInfo}
            stageInfo={stageInfo}
          />
        )}
        {activeTab === 'standards' && <CourseStandards />}
        {activeTab === 'cover' && <CoverImageUploader 
          coverUrl={lesson.cover_image_url} 
          lessonId={lesson.id}
          lessonTitle={lesson.title} 
          learningObjectives={lesson.learning_objectives}
          courseId={courseId}
          onCoverGenerated={(imageUrl) => {
            lesson.cover_image_url = imageUrl;
            window.location.reload();
          }} 
        />}
        {activeTab === 'background' && <BackgroundImageUploader 
          coverUrl={lesson.cover_image_url} 
          backgroundUrl={lesson.bg_image_url}
          lessonId={lesson.id}
          lessonTitle={lesson.title} 
          learningObjectives={lesson.learning_objectives}
          courseId={courseId}
          onBackgroundGenerated={(imageUrl) => {
            lesson.bg_image_url = imageUrl;
          }} 
        />}
      </div>
    </div>
  );
};
